import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, FileText, Video } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { DashboardShell } from "../../components/DashboardShell";
import { StatusPill } from "../../components/StatusPill";
import { Button } from "../../components/ui/button";

const SEV_STYLE = (sev = "") => {
  const s = sev.toLowerCase();
  if (s.includes("severe") || s.includes("significant") || s.includes("positive")) return "text-[#8C2A2A] font-semibold";
  if (s.includes("moderate") || s.includes("elevated")) return "text-[#B8860B] font-semibold";
  return "text-[#2C5A2E] font-semibold";
};

export default function CaseDetail() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [b, setB] = useState(null);

  useEffect(() => {
    api.get(`/clinician/cases/${caseId}`).then((r) => setB(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [caseId]);

  if (!b) return <DashboardShell title="Case"><div className="h-8 w-8 rounded-full border-2 border-[#1E3F20] border-t-transparent animate-spin" /></DashboardShell>;

  const intake = b.intake || {};

  return (
    <DashboardShell title={b.client?.name} subtitle={`${b.product?.name} · Referral: ${b.referral_question || "—"}`}
      actions={<div className="flex items-center gap-3"><StatusPill status={b.status} />
        <Button data-testid="open-report-builder-btn" onClick={() => navigate(`/clinician/cases/${caseId}/report`)}
          className="rounded-full bg-[#1E3F20] hover:bg-[#2C5A2E] text-white transition-colors">
          <FileText className="h-4 w-4 mr-2" /> Report builder
        </Button></div>}>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          {b.flags?.length > 0 && (
            <div className="rounded-xl border border-[#8C2A2A]/30 bg-[#8C2A2A]/5 p-6" data-testid="red-flags-panel">
              <p className="flex items-center gap-2 font-semibold text-[#8C2A2A]"><AlertTriangle className="h-4 w-4" /> Clinical flags</p>
              <ul className="mt-3 space-y-1.5 text-sm text-[#8C2A2A]">
                {b.flags.map((f, i) => <li key={i} data-testid={`flag-${i}`}>• {f}</li>)}
              </ul>
            </div>
          )}

          <section className="card-warm p-6">
            <h2 className="font-heading text-xl font-semibold mb-4">Assessment results</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {(b.responses || []).map((r) => (
                <div key={r.measure} className="border border-[#E5E5DF] rounded-lg p-4" data-testid={`score-card-${r.measure}`}>
                  <p className="text-xs tracking-[0.15em] uppercase text-[#5C5C57] font-bold">{r.measure_name}</p>
                  <p className="font-heading text-3xl font-semibold mt-1">{r.score}<span className="text-base text-[#5C5C57]">{r.max_score ? ` / ${r.max_score}` : ""}</span></p>
                  <p className={`text-sm mt-1 ${SEV_STYLE(r.severity)}`}>{r.severity}</p>
                </div>
              ))}
              {(!b.responses || b.responses.length === 0) && <p className="text-sm text-[#5C5C57]">No questionnaires completed yet.</p>}
            </div>
            {b.results_summary && (
              <div className="mt-5 rounded-lg bg-[#E3DAC9]/30 p-4">
                <p className="text-xs tracking-[0.15em] uppercase font-bold text-[#5C5C57] mb-2">Auto-generated results draft (edit in the report)</p>
                <p className="text-sm whitespace-pre-wrap leading-6" data-testid="results-summary">{b.results_summary}</p>
              </div>
            )}
          </section>

          <section className="card-warm p-6">
            <h2 className="font-heading text-xl font-semibold mb-4">Intake summary</h2>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 text-sm" data-testid="intake-summary">
              {[
                ["Specific question", intake.specific_question],
                ["Occupation", intake.occupation], ["Employer / school", intake.employer_or_school],
                ["Diagnoses", intake.diagnoses], ["Medications", intake.current_medications],
                ["Prior treatment", intake.prior_treatment], ["Hospitalizations", intake.hospitalizations],
                ["Prior evaluations", intake.prior_evaluations],
                ["Documentation requested", (intake.documentation_requested || []).join(", ")],
                ["Records available", (intake.records_available || []).join(", ")],
              ].map(([k, v]) => (
                <div key={k}><dt className="text-xs tracking-[0.15em] uppercase font-bold text-[#5C5C57]">{k}</dt>
                  <dd className="mt-1">{v || "—"}</dd></div>
              ))}
            </dl>
          </section>
        </div>

        <div className="space-y-6">
          <section className="card-warm p-6" data-testid="appointment-panel">
            <h2 className="font-heading text-xl font-semibold mb-3">Interview</h2>
            {b.appointment ? (
              <>
                <p className="text-sm font-semibold">{new Date(b.appointment.start).toLocaleString()}</p>
                <p className="text-xs text-[#5C5C57] mt-1">{b.appointment.duration} minutes · secure video</p>
                <a href={b.appointment.meeting_link} target="_blank" rel="noreferrer" data-testid="clinician-meeting-link">
                  <Button className="mt-4 w-full rounded-full bg-[#1E3F20] hover:bg-[#2C5A2E] text-white transition-colors">
                    <Video className="h-4 w-4 mr-2" /> Join video interview
                  </Button>
                </a>
              </>
            ) : <p className="text-sm text-[#5C5C57]">Not scheduled.</p>}
          </section>
          <section className="card-warm p-6" data-testid="interview-panel">
            <h2 className="font-heading text-xl font-semibold mb-3">Clinical interview</h2>
            <p className="text-xs text-[#5C5C57] mb-4">Conduct the interview in-platform: record & transcribe the conversation (or run the AI-assisted adaptive interview), then review structured findings and apply them to the report.</p>
            <Button onClick={() => navigate(`/clinician/cases/${caseId}/interview`)} data-testid="open-interview-btn"
              variant="outline" className="w-full rounded-full border-[#1E3F20]/30 text-[#1E3F20] hover:bg-[#E3DAC9]/40 transition-colors">
              Open interview room
            </Button>
          </section>
          <section className="card-warm p-6">
            <h2 className="font-heading text-xl font-semibold mb-3">Client</h2>
            <p className="text-sm font-semibold">{b.client?.name}</p>
            <p className="text-xs text-[#5C5C57] mt-1">{b.client?.email}</p>
            {intake.dob && <p className="text-xs text-[#5C5C57] mt-1">DOB: {intake.dob}</p>}
            {intake.phone && <p className="text-xs text-[#5C5C57] mt-1">{intake.phone}</p>}
          </section>
        </div>
      </div>
    </DashboardShell>
  );
}
